import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { FilterComponent } from '../FilterComponent';
import clsx from 'clsx';
import { CheckPicker, DateRangePicker } from 'rsuite';
import { getFormattedDate, makeSearchParams } from '../../utils/utilities';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { RenderExtraFooter } from '../filter/RenderExtraFooter';
import { useDisputesContext } from '../../store/disputes/disputes-context';
import { ButtonComponent } from '../ButtonComponent';
import { NumberFilterComponent } from '../filter/TextFilterComponent';
import { isEqual } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';

export interface ITransactionsFilters {
	query?: string;
	from?: Date | string;
	to?: Date | string;
	provider?: string[];
	type?: string[];
	status?: string[];
	arn?: string;
	amount?: string;
	bin?: string;
	last4?: string;
	orderId?: string;
	transactionId?: string;
	externalReferenceId?: string;
	authCode?: string;
	pageSize?: string;
	pageNumber?: string;
	id?: string
}

interface Props {
	from: Date | string;
	to: Date | string;
	provider: string[];
	type: string[];
	status: string[];
	arn: string;
	amount: string;
	bin: string;
	last4: string;
	orderId: string;
	transactionId: string;
	externalReferenceId: string;
	query: string;

	setFrom: (value: Date | string) => void;
	setTo: (value: Date | string) => void;
	setProvider: (value: string[]) => void;
	setType: (value: string[]) => void;
	setStatus: (value: string[]) => void;
	setArn: (value: string) => void;
	setAmount: (value: string) => void;
	setBin: (value: string) => void;
	setLast4: (value: string) => void;
	setOrderId: (value: string) => void;
	setTransactionId: (value: string) => void;
	setExternalReferenceId: (value: string) => void;
	setQuery: (value: string) => void;

	appliedFiltersState: ITransactionsFilters;
	setAppliedFiltersState: (value: ITransactionsFilters) => void;
	isTranslationDataLoading: boolean;
	initialFilterState: ITransactionsFilters;
}

export const TransactionsPageFilter: FC<Props> = (props) => {

	const {
		from, to, provider, type, status, arn, amount,
		bin, last4, orderId, transactionId, externalReferenceId, query,
		setFrom, setTo, setProvider, setType, setStatus, setArn, setAmount, setBin, setLast4, setOrderId,
		setTransactionId, setExternalReferenceId, setQuery, initialFilterState,
		appliedFiltersState, setAppliedFiltersState, isTranslationDataLoading
	} = props;

	const picker = useRef(null);
	const typesPicker = useRef(null);
	const statusesPicker = useRef(null);
	const arnRef = useRef(null);
	const amountRef = useRef(null);
	const binRef = useRef(null);
	const last4Ref = useRef(null);
	const orderIdRef = useRef(null);
	const transactionIdRef = useRef(null);
	const externalIdRef = useRef(null);
	const { t } = useTranslation();
	const { pathname, state } = useLocation();
	const navigate = useNavigate();

	const { disputesContextState: { disputeDetailsLists }} = useDisputesContext();

	const [selectedFiltersState, setSelectedFiltersState] = useState<ITransactionsFilters>(initialFilterState);

	useEffect(() => {
		setSelectedFiltersState(initialFilterState);
	}, [
		query, provider, from, type, status, arn, amount, bin,
		last4, orderId, transactionId, externalReferenceId,
	]);

	const allProviders = disputeDetailsLists?.providers?.map(item => item.value);
	const allTypes = disputeDetailsLists?.types?.map(item => item.value);
	const allStatuses = disputeDetailsLists?.statuses?.map(item => item.value);

	const handleCheckAllProviders = (value, checked): void => {
		const selectedValues = checked ? allProviders : [];
		setProvider(selectedValues);
	};

	const handleCheckAllTypes = (value, checked): void => {
		const selectedValues = checked ? allTypes : [];
		setType(selectedValues);
	};

	const handleCheckAllStatuses = (value, checked): void => {
		const selectedValues = checked ? allStatuses : [];
		setStatus(selectedValues);
	};

	const handleSaveProviders = (): void => picker.current?.close();
	const handleSaveTypes = (): void => typesPicker.current?.close();
	const handleSaveStatuses = (): void => statusesPicker.current?.close();

	const isSelectedFilters = from
		|| query || arn || amount || bin || last4
		|| provider.length || status.length || type.length
		|| orderId || transactionId || externalReferenceId;

	const handleSearch = async (): Promise<void> => {
		setAppliedFiltersState(initialFilterState);
		navigate(`${pathname}?${makeSearchParams(initialFilterState)}`, { state: { ...state }});
	};

	const handleCleanFilter = (): void => {
		if (isSelectedFilters) {
			setQuery('');
			setProvider([]);
			setStatus([]);
			setType([]);
			setAmount('');
			setArn('');
			setBin('');
			setLast4('');
			setOrderId('');
			setTransactionId('');
			setExternalReferenceId('');
			setFrom(null);
			setTo(null);
		}
	};

	return (
		<div className="card bg-transparent">
			<div className="card-body py-0 px-0">
				<div className="d-flex align-items-center block-width application-selector mb-5">
					<SearchInputSmall
						placeholder={t('TRANSACTIONS.SEARCH_PLACEHOLDER')}
						additionalClass=""
						searchValue={query}
						setSearchValue={setQuery}
					/>

					<ButtonComponent
						additionalClass="btn-sm w-100px d-none d-sm-block ms-10"
						onButtonClick={handleSearch}
						isDisabled={isTranslationDataLoading || isEqual(initialFilterState, appliedFiltersState)}
						loading={isTranslationDataLoading}
					>
						<>
							{
								isEqual(selectedFiltersState, appliedFiltersState)
									? 'Search'
									: (
										<div className="d-flex align-items-center justify-content-center">
											<FontAwesomeIcon icon={faTriangleExclamation} />
											<span className="ms-2 fw-bolder">Refresh</span>
										</div>
									)
							}
						</>
					</ButtonComponent>

				</div>
				<div className="d-flex align-items-center fc-filter-line">
					<FilterComponent value={from} label="Created date" labelPlacement="integrated">
						<div
							className={clsx('fc-datepicker fc-range-datepicker-without-time', from && 'fc-added-icon')}>
							<DateRangePicker
								placeholder="Created date"
								placement="auto"
								onChange={(value): void => {
									const dateFrom = value?.[0] ? getFormattedDate(value[0], 'yyyy-MM-dd HH:mm:ss') : null;
									const dateTo = value?.[1] ? getFormattedDate(value[1], 'yyyy-MM-dd HH:mm:ss') : null;
									setFrom(dateFrom);
									setTo(dateTo);
								}}
								onClean={(): void => {
									setFrom(null);
									setTo(null);
								}}
								shouldDisableDate={(date): boolean => isAfter(date, new Date())}
								value={!from || !to ? null : [new Date(from), new Date(to)]}
								showOneCalendar
								disabled={isTranslationDataLoading}
							/>
						</div>
					</FilterComponent>
					<FilterComponent value={provider?.length} label="Providers" labelPlacement="integrated">
						<CheckPicker
							data={disputeDetailsLists?.providers ?? []}
							ref={picker}
							searchable={false}
							value={provider}
							onChange={(value): void => setProvider(value)}
							onClean={(): void => setProvider([])}
							placeholder='Providers'
							className={clsx('fc-selector picker-selector', !provider?.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							disabled={isTranslationDataLoading}
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={provider}
									allValue={allProviders}
									handleCheckAll={handleCheckAllProviders}
									handleSave={handleSaveProviders}
								/>
							)}
						/>
					</FilterComponent>
					<FilterComponent value={type.length} label="Types" labelPlacement="integrated">
						<CheckPicker
							data={disputeDetailsLists?.types || []}
							ref={typesPicker}
							searchable={false}
							value={type}
							onChange={(value): void => setType(value)}
							onClean={(): void => setType([])}
							placeholder='Types'
							className={clsx('fc-selector picker-selector', !type.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							disabled={isTranslationDataLoading}
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={type}
									allValue={allTypes}
									handleCheckAll={handleCheckAllTypes}
									handleSave={handleSaveTypes}
								/>
							)}
						/>
					</FilterComponent>
					<FilterComponent value={status?.length} label="Statuses" labelPlacement="integrated">
						<CheckPicker
							data={disputeDetailsLists?.statuses || []}
							ref={statusesPicker}
							searchable={false}
							value={status}
							onChange={(value): void => setStatus(value)}
							onClean={(): void => setStatus([])}
							placeholder='Statuses'
							className={clsx('fc-selector picker-selector', !status?.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomEnd"
							disabled={isTranslationDataLoading}
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={status}
									allValue={allStatuses}
									handleCheckAll={handleCheckAllStatuses}
									handleSave={handleSaveStatuses}
								/>
							)}
						/>
					</FilterComponent>
					<FilterComponent value={arn} isLoading={isTranslationDataLoading} label="Arn" labelPlacement="integrated">
						<NumberFilterComponent
							reference={arnRef}
							fieldLabel='Arn'
							close={(): void => arnRef.current.close()}
							open={(): void => arnRef.current.open()}
							value={arn}
							setValue={setArn}
							type="number"
							isLoading={isTranslationDataLoading}
						/>
					</FilterComponent>
					<FilterComponent value={amount} isLoading={isTranslationDataLoading} label="Amount" labelPlacement="integrated">
						<NumberFilterComponent
							reference={amountRef}
							fieldLabel='Amount'
							close={(): void => amountRef.current.close()}
							open={(): void => amountRef.current.open()}
							value={amount}
							setValue={setAmount}
							type="number"
							isLoading={isTranslationDataLoading}
							isNumber
						/>
					</FilterComponent>
					<FilterComponent value={bin} isLoading={isTranslationDataLoading} label="Bin" labelPlacement="integrated">
						<NumberFilterComponent
							reference={binRef}
							fieldLabel='Bin'
							close={(): void => binRef.current.close()}
							open={(): void => binRef.current.open()}
							value={bin}
							setValue={setBin}
							type="number"
							isLoading={isTranslationDataLoading}
							isBin
						/>
					</FilterComponent>
					<FilterComponent value={last4} isLoading={isTranslationDataLoading} label="Last4" labelPlacement="integrated">
						<NumberFilterComponent
							reference={last4Ref}
							fieldLabel='Last4'
							close={(): void => last4Ref.current.close()}
							open={(): void => last4Ref.current.open()}
							value={last4}
							setValue={setLast4}
							type="number"
							isLoading={isTranslationDataLoading}
							isLast4
						/>
					</FilterComponent>
					<FilterComponent value={orderId} isLoading={isTranslationDataLoading} label="Order id" labelPlacement="integrated">
						<NumberFilterComponent
							reference={orderIdRef}
							fieldLabel='Order id'
							close={(): void => orderIdRef.current.close()}
							open={(): void => orderIdRef.current.open()}
							value={orderId}
							setValue={setOrderId}
							type="text"
							isLoading={isTranslationDataLoading}
							isGuid
						/>
					</FilterComponent>
					<FilterComponent value={transactionId} isLoading={isTranslationDataLoading} label="Transaction id" labelPlacement="integrated">
						<NumberFilterComponent
							reference={transactionIdRef}
							fieldLabel='Transaction id'
							close={(): void => transactionIdRef.current.close()}
							open={(): void => transactionIdRef.current.open()}
							value={transactionId}
							setValue={setTransactionId}
							type="text"
							isLoading={isTranslationDataLoading}
							isGuid
						/>
					</FilterComponent>
					<FilterComponent value={externalReferenceId} isLoading={isTranslationDataLoading} label="External reference id" labelPlacement="integrated">
						<NumberFilterComponent
							reference={externalIdRef}
							fieldLabel='External reference id'
							close={(): void => externalIdRef.current.close()}
							open={(): void => externalIdRef.current.open()}
							value={externalReferenceId}
							setValue={setExternalReferenceId}
							type="text"
							isLoading={isTranslationDataLoading}
						/>
					</FilterComponent>

					<div
						className={clsx(
						'text-danger text-decoration-underline',
							isSelectedFilters && !isTranslationDataLoading ? 'cursor-pointer' : 'cursor-not-allowed')
						}
						onClick={(): void => !isTranslationDataLoading && handleCleanFilter()}
					>
						Clear filters
					</div>
				</div>
			</div>
		</div>
	);
};
